import moment from 'moment'
import { formatPrice, getToFixed2Number, User } from 'utilities'
import { Series } from '../MSLine/MSLine.types'
import { ViewerChartPeriod } from './types'
import { buildChartTooltipHtml, chartTooltipBase } from '../chartTooltip'
import { CHART_COLORS, CHART_FONT_FAMILY, toChartNumber } from '../chartTheme'

const moneyLabel = User.isShopOwner() ? 'expenses' : 'revenue'

export interface DualAxisSeriesConfig extends Series {
  renderAs: 'bar' | 'area' | 'line'
  parentYAxis: 'S' | 'Y'
  showAnchors?: string
}

export const dualAxisSeriesConfig: DualAxisSeriesConfig[] = [
  {
    seriesname: 'Add ' + moneyLabel,
    color: '#70D6C6',
    anchorBgColor: '#70D6C6',
    renderAs: 'bar',
    parentYAxis: 'S',
    data: []
  },
  {
    seriesname: 'Purchase revenue',
    color: '#254291',
    anchorBgColor: '#254291',
    renderAs: 'bar',
    parentYAxis: 'S',
    data: []
  },
  {
    seriesname: 'Total watch time',
    renderAs: 'area',
    alpha: '30',
    parentYAxis: 'Y',
    showAnchors: '0',
    anchorBgColor: '#3AABF2',
    color: '#3AABF2',
    data: []
  },
  {
    seriesname: 'Total video interaction time',
    parentYAxis: 'Y',
    renderAs: 'line',
    anchorBgColor: '#457570',
    color: '#457570',
    data: []
  },
  {
    seriesname: 'Total page interaction time',
    parentYAxis: 'Y',
    renderAs: 'line',
    anchorBgColor: '#FFFFFF',
    color: '#3AABF2',
    data: []
  },
  {
    seriesname: 'Total VOV interaction time',
    parentYAxis: 'Y',
    renderAs: 'line',
    anchorBgColor: '#FFFFFF',
    color: '#38A19D',
    data: []
  },
  {
    seriesname: 'Total VOV watch time',
    parentYAxis: 'Y',
    renderAs: 'line',
    anchorBgColor: '#FFFFFF',
    color: '#5E38A1',
    data: []
  }
]

const isMoneySeries = (seriesName: string): boolean => {
  const name = (seriesName || '').toLowerCase()
  return name.includes('revenue') || name.includes('expenses')
}

const formatDualAxisValue = (seriesName: string, value: number): string => {
  if (isMoneySeries(seriesName)) {
    return formatPrice(value)
  }
  return `${getToFixed2Number(value)} hrs`
}

export const buildDualAxisChartModel = (data: Map<String, ViewerChartPeriod>) => {
  const category: { label: string }[] = []
  const seriesData: number[][] = dualAxisSeriesConfig.map(() => [])

  Array.from(data.keys()).forEach((key) => {
    const period = data.get(key)
    category.push({ label: String(key) })
    seriesData[0].push(toChartNumber(period && period.revenue))
    seriesData[1].push(toChartNumber(period && period.totalOrders))
    seriesData[2].push(moment.duration(period && period.totalWatchTime, 'milliseconds').asHours())
    seriesData[3].push(moment.duration(period && period.totalInteractionTime, 'milliseconds').asHours())
    seriesData[4].push(moment.duration(period && period.totalPageInteractionTime, 'milliseconds').asHours())
    seriesData[5].push(moment.duration(period && period.totalVovInteractionTime, 'milliseconds').asHours())
    seriesData[6].push(moment.duration(period && period.totalVovWatchTime, 'milliseconds').asHours())
  })

  const dataset: DualAxisSeriesConfig[] = dualAxisSeriesConfig.map((series, index) => ({
    ...series,
    data: seriesData[index].map((value) => ({ value }))
  }))

  return { category, dataset }
}

export const buildDualAxisOption = (
  category: { label: string }[],
  dataset: DualAxisSeriesConfig[]
) => {
  const yAxisName = 'Hours'
  const syAxisName = 'USD'

  const series = dataset.map((item) => {
    const yAxisIndex = item.parentYAxis === 'S' ? 1 : 0
    const values = (item.data || []).map((entry) => toChartNumber(entry.value))
    const common = {
      name: item.seriesname,
      yAxisIndex,
      data: values,
      itemStyle: { color: item.color },
      emphasis: { focus: 'series' }
    }

    if (item.renderAs === 'bar') {
      return {
        ...common,
        type: 'bar' as const,
        barMaxWidth: 18
      }
    }

    if (item.renderAs === 'area') {
      return {
        ...common,
        type: 'line' as const,
        symbol: item.showAnchors === '0' ? 'none' : 'circle',
        symbolSize: 10,
        lineStyle: { color: item.color, width: 2 },
        areaStyle: {
          color: item.color,
          opacity: item.alpha ? Number(item.alpha) / 100 : 0.3
        }
      }
    }

    return {
      ...common,
      type: 'line' as const,
      symbol: 'circle',
      symbolSize: 10,
      lineStyle: { color: item.color, width: 2 }
    }
  })

  return {
    backgroundColor: 'transparent',
    legend: {
      top: 0,
      left: 0,
      icon: 'circle',
      itemWidth: 10,
      itemHeight: 10,
      textStyle: {
        color: CHART_COLORS.primaryContent,
        fontFamily: CHART_FONT_FAMILY,
        fontSize: 12
      }
    },
    grid: {
      left: 16,
      right: 48,
      top: 48,
      bottom: 8,
      containLabel: true
    },
    tooltip: {
      ...chartTooltipBase,
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        lineStyle: { color: CHART_COLORS.primaryContent }
      },
      formatter: (params: any) => {
        const items = Array.isArray(params) ? params : [params]
        const categoryLabel = items[0] && items[0].axisValueLabel
        return buildChartTooltipHtml(
          {
            label: `${yAxisName} / ${syAxisName}`,
            value: categoryLabel
          },
          items.map((item) => {
            const seriesMeta = dataset[item.seriesIndex]
            return {
              label: seriesMeta && seriesMeta.seriesname || item.seriesName,
              color: seriesMeta && (seriesMeta.anchorBgColor || seriesMeta.color) || item.color,
              image: seriesMeta && seriesMeta.image,
              value: formatDualAxisValue(
                seriesMeta && seriesMeta.seriesname || item.seriesName,
                toChartNumber(item.value)
              )
            }
          })
        )
      }
    },
    xAxis: {
      type: 'category',
      name: 'Period',
      nameLocation: 'middle',
      nameGap: 22,
      nameTextStyle: {
        color: CHART_COLORS.placeholderContent,
        fontFamily: CHART_FONT_FAMILY,
        fontSize: 12
      },
      data: category.map((entry) => entry.label),
      axisTick: { show: false },
      axisLine: { lineStyle: { color: CHART_COLORS.gridLine } },
      axisLabel: {
        color: CHART_COLORS.primaryContent,
        fontFamily: CHART_FONT_FAMILY,
        fontSize: 12,
        hideOverlap: true
      }
    },
    yAxis: [
      {
        type: 'value',
        name: yAxisName,
        nameTextStyle: {
          color: CHART_COLORS.placeholderContent,
          fontFamily: CHART_FONT_FAMILY,
          fontSize: 12
        },
        splitNumber: 8,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: CHART_COLORS.gridLine } },
        axisLabel: {
          color: CHART_COLORS.primaryContent,
          fontFamily: CHART_FONT_FAMILY,
          fontSize: 12
        }
      },
      {
        type: 'value',
        name: syAxisName,
        nameTextStyle: {
          color: CHART_COLORS.placeholderContent,
          fontFamily: CHART_FONT_FAMILY,
          fontSize: 12
        },
        splitNumber: 8,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: {
          color: CHART_COLORS.primaryContent,
          fontFamily: CHART_FONT_FAMILY,
          fontSize: 12
        }
      }
    ],
    series
  }
}
